<script lang="ts" setup>
import { computed, ComputedRef, inject, PropType, ref } from 'vue';
import {
  Tabs,
  Tab,
  TirPmButton,
  TirPmButtonVariantEnum,
  TirPmButtonSizeEnum,
  PopoverPositionEnum,
  TirPmScrollbar,
  TirPmTabsVariantEnum,
} from 'tir-components';
import { CurrentUser, UserRoleEnum } from 'tir-pm-user-core';
import { PlusIcon } from 'tir-style-system/icons/outline';
import { QueueRelationModel, SubtaskModel } from '@/components/task-forms/models';
import { TaskQueueSearchDropdown, TaskSubtasksSearchDropdown } from '@/components/task-forms';
import { QueuesList, SubtasksList } from './components';
import { AdapterService } from './adapter-service';
import { TaskModel } from '../task-drawer/models';
import GetAccessCurrentUserInQueueTaskByIdResponseModel from '../task-detail/models/GetAccessCurrentUserInQueueTaskByIdResponseModel';
import { EmployeeQueueTaskAccessEnum } from '../task-detail/enums';

/**API сервис */
const apiService = new AdapterService();

/**Параметры */
const props = defineProps({
  /**Редактируемая задача */
  editTaskInfo: { type: Object as PropType<TaskModel> },
  /**id проекта */
  projectId: { type: Number, required: true },
  /**Функция для поиска подзадач */
  tasksSearchFunction: { type: Function },
  /**Id задачи */
  taskId: { type: Number },
  /**Id текущей очереди */
  queueId: { type: String },
  /**Список очередей задачи */
  queues: { type: Array as PropType<QueueRelationModel[]> },
  /**Подзадачи */
  subtasks: { type: Array as PropType<SubtaskModel[]> },
  /**Режим редактирования */
  isEdit: { type: Boolean, default: true },
  /**Внутри шторки */
  inDrawer: { type: Boolean, default: false },
  /**Доступы юзера к очереди */
  userAccess: { type: Object as PropType<GetAccessCurrentUserInQueueTaskByIdResponseModel> },
});
/**События */
const emit = defineEmits<{
  /**Создана подзадача */
  createdSubtask: [taskId: number];
  /**Обновлена подзадача */
  updatedSubtask: [taskId: number];
  /**Добавление подзадачи */
  attachSubtask: [taskId: number];
  /**Удалить подзадачу */
  removeSubtask: [taskId: number];
  /**Добавление очереди */
  attachQueue: [];

  subtaskItem: [];
  removeSubtaskItem: [];
}>();

/**Владелец */
const isOwner = computed(() => CurrentUser.Roles.includes(UserRoleEnum.Owner));
/**Админ проекта */
const isProjectAdmin = inject('isAdminProject') as ComputedRef<boolean>;

/**Текущий таб */
const currentTab = ref(0);
/**Ссылка на скроллбар */
const $scrollbar = ref<InstanceType<typeof TirPmScrollbar>>();
/**Ссылка на подзадачи */
const $subtasks = ref<InstanceType<typeof SubtasksList>>();
/**Ссылка на очереди */
const $queues = ref<InstanceType<typeof QueuesList>>();
/**id выбранной очереди */
const selectedQueueId = ref();

/**Последний элемент подзадачи */
const lastElementSubtask = computed(() => {
  return $subtasks.value.subtask.length - 1;
});
/**Подзадача */
const subtaskElement = computed(() => {
  return $subtasks.value.subtask[lastElementSubtask.value].subtaskItem;
});

/**Открыт таб подзадач */
const isSubtasksTab = computed(() => currentTab.value == 0);
/**Компонент выпадающего списка */
const dropdownComponent = computed(() =>
  isSubtasksTab.value ? TaskSubtasksSearchDropdown : TaskQueueSearchDropdown,
);
/**Отображать кнопку добавления */
const isVisibleAddButton = computed(() => {
  // Для очередей
  if (currentTab.value === 1) {
    // Добавлять очереди могут владелец, админ проекта
    return isOwner.value || isProjectAdmin.value;
  }
  return true;
});
/**Функция поиска очереди/задачи */
const searchFunction = computed(() =>
  isSubtasksTab.value ? props.tasksSearchFunction : apiService.getQueues.bind(apiService),
);
/**Id уже добавленных задач */
const ignoreTasks = computed(() => props.subtasks.map((s) => s.Id));
/**Отображение кнопки добавления подзадач */
const isShowAddSubtaskButton = computed(
  () =>
    props.userAccess.IsOwner ||
    props.userAccess.IsNamespaceAdmin ||
    props.userAccess.IsProjectAdmin ||
    props.userAccess.EmployeeQueueTaskAccesses.includes(EmployeeQueueTaskAccessEnum.CreateTask),
);

/**Спуститься до задачи */
const scrollToLastElement = (element: HTMLElement) => {
  const scrollContainer = $scrollbar.value?.$scroll;

  const currentScrollTop = scrollContainer.scrollTop;
  const targetScrollTop =
    currentScrollTop +
    element.getBoundingClientRect().top -
    scrollContainer.getBoundingClientRect().top -
    20;

  scrollContainer.scrollTo({
    top: targetScrollTop,
    behavior: 'smooth',
  });
};
/**Удалить подзадачу */
const removeSubtask = (taskId: number) => {
  const removeSubtaskIndex = props.subtasks.findIndex((q) => q.Id == taskId);
  if (removeSubtaskIndex == -1) return;

  emit('removeSubtask', taskId);
  emit('removeSubtaskItem');
  props.queues.splice(removeSubtaskIndex, 1);
};
/**Удалить задачу из очереди */
const removeQueue = (queueId: string) => {
  const removeQueueIndex = props.queues.findIndex((q) => q.Id == queueId);
  if (removeQueueIndex == -1) return;

  apiService.removeQueueRelation(props.taskId, queueId);
  props.queues.splice(removeQueueIndex, 1);
};
/**При выборе задачи/очереди */
const onSelectItem = async (data: number | QueueRelationModel) => {
  if (isSubtasksTab.value && typeof data == 'number') {
    emit('attachSubtask', data);
    emit('subtaskItem');
  } else {
    emit('attachQueue');
    data = data as QueueRelationModel;
    await apiService.addQueueRelation(props.taskId, data.Id);
    props.queues.push(data);
    selectedQueueId.value = data.Id;
  }
};

defineExpose({
  subtaskElement,
  scrollToLastElement,
});
</script>
<template>
  <div class="task-connections">
    <Tabs
      class="task-connections_tabs"
      v-model="currentTab"
      :variant="TirPmTabsVariantEnum.Secondary"
    >
      <Tab label="Подзадачи" :val="0" :current-tab="currentTab">
        <span v-text="subtasks?.length || 0" class="task-connections_count" />
      </Tab>
      <Tab label="Очереди" :val="1" :current-tab="currentTab">
        <span v-text="props.queues?.length || 0" class="task-connections_count" />
      </Tab>
    </Tabs>
    <Component
      v-if="isVisibleAddButton && isEdit"
      class="task-connections_dropdown"
      :editTaskInfo="editTaskInfo"
      :is="dropdownComponent"
      staticScrollHeight
      :inDrawer="inDrawer"
      :searchFunction="searchFunction"
      :is-disabled="!isEdit"
      :ignore-tasks="ignoreTasks"
      :custom-popover-position="PopoverPositionEnum.Top"
      :task-id="taskId"
      :task-queue-id="queueId"
      :allow-create-subtask="!!taskId"
      @on-select="onSelectItem"
      @on-created-subtask="emit('createdSubtask', $event)"
      @on-updated-subtask="emit('updatedSubtask', $event)"
    >
      <template #target>
        <TirPmButton
          v-if="isShowAddSubtaskButton"
          class="task-connections_add"
          :variant="TirPmButtonVariantEnum.LinkState"
          :size="TirPmButtonSizeEnum.Small"
        >
          <PlusIcon />
          Добавить
        </TirPmButton>
      </template>
    </Component>
    <SubtasksList
      v-if="currentTab == 0"
      ref="$subtasks"
      :projectId="projectId"
      :subtasks="subtasks"
      :queueId="queueId"
      @remove="removeSubtask"
      @updatedSubtask="emit('updatedSubtask', $event)"
      :editing="isEdit"
    />
    <QueuesList
      v-else-if="currentTab == 1"
      ref="$queues"
      :queues="queues"
      :projectId="projectId"
      :scrollbar="$scrollbar"
      :selectedQueueId="selectedQueueId"
      @remove="removeQueue"
      :editing="isEdit"
    />
  </div>
</template>
<style lang="scss">
.task-connections {
  &_dropdown {
    width: 100% !important;
  }

  &_tabs {
    margin-bottom: 0.75rem;
  }

  &_add {
    &.button--small {
      min-width: 100%;
      margin-bottom: 0.25rem;
      font-size: 0.875rem;
      font-weight: 500;
    }

    .button__sign-main {
      display: flex;
      align-items: center;
    }

    svg {
      width: 1rem;
      height: 1rem;
    }
  }

  .tab-item {
    .task-connections_count {
      color: var(--text-secondary);
      margin-left: 0.5rem;
    }

    &--active {
      .task-connections_count {
        color: var(--text-inverted-secondary);
      }
    }
  }
}
</style>
